// Le marcature del markdown, applicate a una selezione.
//
// Ognuna è una coppia di delimitatori, tranne il sottolineato: **il markdown
// non ha il sottolineato**. Non è una dimenticanza di questa app — è che nella
// tipografia il sottolineato è un ripiego della macchina da scrivere, e il
// markdown non lo prevede. pandoc lo sa fare con uno span attribuito
// (`[testo]{.underline}`), che è la forma che questo bottone scrive: composta
// dà un vero sottolineato, e resta leggibile nel sorgente.

import { EditorSelection, type EditorState } from "@codemirror/state";
import { type EditorView } from "@codemirror/view";


export const MARCATURE = ["grassetto", "corsivo", "sottolineato", "sbarrato"] as const;

export type Marcatura = (typeof MARCATURE)[number];

interface Forma
{
    prima: string;
    dopo: string;
}

const FORME: Record<Marcatura, Forma> = {
    grassetto: { prima: "**", dopo: "**" },
    corsivo: { prima: "*", dopo: "*" },
    sottolineato: { prima: "[", dopo: "]{.underline}" },
    sbarrato: { prima: "~~", dopo: "~~" },
};


export function marca(view: EditorView, quale: Marcatura): void
{
    const forma = FORME[quale];

    // Se la selezione è già dentro una marcatura di questo tipo si toglie
    // quella, delimitatori compresi: selezionare la parola dentro `**parola**`
    // e premere Grassetto deve toglierlo, non scrivere `**​**parola**​**`.
    const dentro = marcaturaDellaSelezione(view.state, quale);

    if (dentro !== null)
    {
        togli(view, dentro);
        return;
    }

    view.dispatch(view.state.changeByRange((range) =>
    {
        const testo = view.state.sliceDoc(range.from, range.to);

        // La selezione si rifà a mano su quello che si vede: lasciata mappare
        // dai cambi, dopo un `**` in testa finisce mezza dentro i delimitatori
        // e la marcatura successiva colpisce il testo sbagliato. Dopo l'azione
        // resta selezionato lo STESSO testo di prima, senza delimitatori.
        if (testo.startsWith(forma.prima) && testo.endsWith(forma.dopo)
            && testo.length >= forma.prima.length + forma.dopo.length)
        {
            const nudo = testo.slice(forma.prima.length, testo.length - forma.dopo.length);

            return {
                changes: { from: range.from, to: range.to, insert: nudo },
                range: EditorSelection.range(range.from, range.from + nudo.length),
            };
        }

        // Col cursore e basta i due delimitatori si scrivono vuoti e il
        // cursore resta in mezzo, pronto a scrivere dentro.
        const dentro = range.from + forma.prima.length;

        return {
            changes: { from: range.from, to: range.to, insert: `${forma.prima}${testo}${forma.dopo}` },
            range: EditorSelection.range(dentro, dentro + testo.length),
        };
    }));

    view.focus();
}


// Il riconoscimento all'incontrario: dato un punto, di che marcature fa parte.
//
// Serve al menu contestuale, che deve poter dire «Togli grassetto» quando ci
// clicchi dentro SENZA aver selezionato niente — e' li' che uno se ne accorge
// di volerlo togliere, non quando ha gia' selezionato con cura le due stelle.
// Il riconoscimento all'incontrario: dato un punto, di che marcature fa parte.
//
// Gli asterischi si leggono a RUN, non come coppie separate: `***esempio***` è
// grassetto E corsivo insieme, e cercare `*x*` con un'occhiata che esclude gli
// asterischi vicini non ci trova niente — era il caso in cui «Togli corsivo»
// non compariva. Un run di 1 è corsivo, di 2 grassetto, di 3 tutti e due.
const ASTERISCHI = /(?<!\*)(\*{1,3})([^*\n]+)\1(?!\*)/g;
const SBARRATO = /~~([^~\n]+)~~/g;
const SOTTOLINEATO = /\[([^\]\n]+)\]\{\.underline\}/g;


export interface Trovata
{
    quale: Marcatura;
    da: number;
    a: number;
    /** Il testo senza i delimitatori di NESSUNA marcatura. */
    nudo: string;
    /** Come diventa il tratto togliendo questa sola marcatura. */
    risultato: string;
    /** Dove finisce `nudo` dentro `risultato`: serve a tenere la selezione sul testo. */
    scarto: number;
}


function daRun(run: number, quale: Marcatura, nudo: string): { risultato: string; scarto: number }
{
    // Da `***x***` togliendo il corsivo resta `**x**`, togliendo il grassetto
    // resta `*x*`: si leva una stella per lato, non tutte.
    const resta = run === 3 ? (quale === "corsivo" ? 2 : 1) : 0;
    const delimitatore = "*".repeat(resta);

    return { risultato: `${delimitatore}${nudo}${delimitatore}`, scarto: resta };
}


/** Le marcature che coprono `pos`, dalla piu' interna alla piu' esterna. */
export function marcatureIn(state: EditorState, pos: number): Trovata[]
{
    const riga = state.doc.lineAt(pos);
    const trovate: Trovata[] = [];

    const aggiungi = (quale: Marcatura, inizio: number, lunghezza: number, nudo: string,
        risultato: string, scarto: number): void =>
    {
        const da = riga.from + inizio;
        const a = da + lunghezza;

        if (pos >= da && pos <= a) { trovate.push({ quale, da, a, nudo, risultato, scarto }); }
    };

    for (const m of riga.text.matchAll(ASTERISCHI))
    {
        const run = (m[1] ?? "").length;
        const nudo = m[2] ?? "";

        for (const quale of run === 1 ? ["corsivo"] as const
            : run === 2 ? ["grassetto"] as const
                : ["grassetto", "corsivo"] as const)
        {
            const { risultato, scarto } = daRun(run, quale, nudo);
            aggiungi(quale, m.index ?? 0, m[0].length, nudo, risultato, scarto);
        }
    }

    for (const m of riga.text.matchAll(SBARRATO))
    {
        aggiungi("sbarrato", m.index ?? 0, m[0].length, m[1] ?? "", m[1] ?? "", 0);
    }

    for (const m of riga.text.matchAll(SOTTOLINEATO))
    {
        aggiungi("sottolineato", m.index ?? 0, m[0].length, m[1] ?? "", m[1] ?? "", 0);
    }

    return trovate.sort((x, y) => (x.a - x.da) - (y.a - y.da));
}


export function togli(view: EditorView, t: Trovata): void
{
    // Tolti i delimitatori resta selezionato il testo che c'era dentro: è
    // quello che stavi guardando, e ci puoi applicare subito un'altra
    // marcatura senza riselezionare.
    view.dispatch({
        changes: { from: t.da, to: t.a, insert: t.risultato },
        selection: EditorSelection.range(t.da + t.scarto, t.da + t.scarto + t.nudo.length),
    });

    view.focus();
}


/**
 * La marcatura di questo tipo che copre TUTTA la selezione corrente, se c'è.
 *
 * Serve a rispondere alla domanda che si fa il menu contestuale: di questo
 * testo, il grassetto lo metto o lo tolgo?
 */
export function marcaturaDellaSelezione(state: EditorState, quale: Marcatura): Trovata | null
{
    const range = state.selection.main;

    return marcatureIn(state, range.from)
        .find((t) => t.quale === quale && t.da <= range.from && t.a >= range.to) ?? null;
}
